/**
 * Utilities for parsing /catalog/filters API response.
 *
 * Supports two response shapes:
 *
 * v3 array format:
 *   { data: [{ name: "lines", items: [{ name: "Line Name", _count: { product: 5 } }] }] }
 *
 * Legacy object format:
 *   { data: { lines: [{ name: "...", value: "...", total: 5 }], categorias: [...] } }
 */

export interface FilterItem {
  value: string;
  label: string;
  count: number;
  picture?: { url?: string };
}

export interface CatalogFilterGroup {
  name: string;
  items: Array<{ name: string; value?: string; _count?: { product?: number; products?: number }; total?: number; count?: number; picture?: { url?: string } }>;
}

export interface ParsedFilterData {
  categories: FilterItem[];
  lines: FilterItem[];
  sizes: FilterItem[];
  colors: FilterItem[];
  priceRange: { min: number; max: number };
  sortOptions: Array<{ value: string; label: string }>;
}

const DEFAULT_SORT_OPTIONS = [
  { value: 'name:asc', label: 'Nombre A-Z' },
  { value: 'name:desc', label: 'Nombre Z-A' },
  { value: 'price:asc', label: 'Precio menor a mayor' },
  { value: 'price:desc', label: 'Precio mayor a menor' },
];

/** Extracts a count from either the v3 _count shape or the legacy total/count field. */
function getItemCount(item: CatalogFilterGroup['items'][number]): number {
  return item._count?.product ?? item._count?.products ?? item.total ?? item.count ?? 0;
}

/**
 * Converts a raw item array to FilterItem[], supporting both v3 and legacy shapes.
 * Items without a positive count are excluded.
 */
function parseItems(items: CatalogFilterGroup['items'] | undefined): FilterItem[] {
  if (!Array.isArray(items)) return [];
  return items
    .filter((item) => getItemCount(item) > 0)
    .map((item) => ({
      value: item.value ?? item.name,
      label: item.name,
      count: getItemCount(item),
      picture: (item as any).picture || { url: 'https://placehold.co/600x400.png' }, // Preserve picture if present (for lines)
    }));
}

/**
 * Normalise the raw API payload into a map of { groupName → items[] }.
 *
 * Handles:
 *  - v3 array: [{ name: "lines", items: [...] }, ...]
 *  - Legacy object: { lines: [...], categorias: [...], ... }
 */
function buildGroupMap(data: unknown): Record<string, CatalogFilterGroup['items']> {
  if (Array.isArray(data)) {
    // v3 format
    return Object.fromEntries((data as CatalogFilterGroup[]).map((g) => [g.name, g.items ?? []]));
  }

  if (data && typeof data === 'object' && !Array.isArray(data)) {
    // Legacy object format – keys may be in Spanish ("categorias") or English ("categories")
    const raw = data as Record<string, unknown>;
    const map: Record<string, CatalogFilterGroup['items']> = {};
    for (const [key, value] of Object.entries(raw)) {
      if (Array.isArray(value)) {
        map[key] = value as CatalogFilterGroup['items'];
      }
    }
    // Alias Spanish key → English key expected by the rest of the code
    if (!map.categories && map.categorias) map.categories = map.categorias;
    return map;
  }

  return {};
}

/**
 * Converts the /catalog/filters API response into a structured object ready
 * for product filter UI components.
 *
 * Supports both the v3 array format and the legacy object format.
 * Only items with a positive product count are included.
 */
export function parseCatalogFilters(data: unknown, options?: { priceRange?: { min?: number; max?: number }; sortOptions?: Array<{ value: string; label: string }> }): ParsedFilterData {
  const map = buildGroupMap(data);

  const filtersData = {
    categories: parseItems(map.categories),
    lines: parseItems(map.lines),
    sizes: parseItems(map.sizes),
    colors: parseItems(map.colors),
    priceRange: {
      min: options?.priceRange?.min ?? 1000,
      max: options?.priceRange?.max ?? 1000000,
    },
    sortOptions: options?.sortOptions ?? DEFAULT_SORT_OPTIONS,
  };

  // //console.warn('Parsed filter data:', filtersData); // Log final parsed filters for debugging

  return filtersData;
}
